import type {
  ArtefactSummary,
  Attachment,
  Folder,
  FolderSection,
  ProjectDocument,
  WikiPageSummary,
} from "@esi/shared";
import { local } from "@/lib/storage";
import { foldTitle } from "@/lib/utils";

/**
 * The knowledge tree (openspec change `knowledge-panel`, design §2–§3, §10): sections, folders
 * and items built on the client from what the API returned for this viewer (documents, wiki
 * index, folders). Nothing here grants or hides access: the server filtered every list, and
 * counts only ever add up what was received.
 */

/**
 * A project document as the tree reads it. `connectorItemId` is set for an item a connector
 * brought in (`sharepoint-connector`); the document list does not return it yet.
 */
export type KnowledgeDocument = ProjectDocument & { connectorItemId?: string | null };

/** `artefacts`: the open conversation's artefacts (`artefacts-viewer`), not project knowledge. */
export type TreeSection = "files" | "wiki" | "connected" | "attachments" | "artefacts";
export type ItemType = "file" | "wiki" | "connected" | "attachment" | "artefact";

type Base = {
  /**
   * Unique across the tree: `s:<section>`, `f:<folderId>`, `d:<documentId>`, `w:<pageId>`,
   * `a:<artefactId>`.
   */
  id: string;
  /** The parent node's ID; null for a section. */
  parentId: string | null;
  section: TreeSection;
  /** The shown name, and its folded form for search ({@link foldTitle}). */
  name: string;
  folded: string;
};
export type SectionNode = Base & { kind: "section"; children: TreeNode[]; count: number };
export type FolderNode = Base & {
  kind: "folder";
  folder: Folder;
  /** 1 for a folder at its section's root, up to `MAX_FOLDER_DEPTH`. */
  depth: number;
  children: TreeNode[];
  /** Items the viewer received under it, recursively (0 for a folder of hidden items only). */
  count: number;
};
export type ItemNode = Base &
  (
    | { kind: "item"; type: "file" | "connected"; document: KnowledgeDocument }
    | { kind: "item"; type: "wiki"; page: WikiPageSummary }
    | { kind: "item"; type: "attachment"; attachment: Attachment }
    | { kind: "item"; type: "artefact"; artefact: ArtefactSummary }
  );
export type TreeNode = SectionNode | FolderNode | ItemNode;
export type BranchNode = SectionNode | FolderNode;

export type KnowledgeTree = {
  /**
   * Wiki, Files (shown as Sources), then Connected once a connected item exists (design §2;
   * wiki first since `wiki-first-navigation`), then the conversation's Artefacts once it has
   * one (`artefacts-viewer`).
   */
  sections: SectionNode[];
  byId: ReadonlyMap<string, TreeNode>;
  hasConnected: boolean;
};

export const sectionId = (section: TreeSection) => `s:${section}`;
export const folderNodeId = (folderId: string) => `f:${folderId}`;

const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: "base" });

/**
 * The tree's order: folders first by name, then items by title (user decision of 2026-10-24).
 * The Wiki's folder index and previous/next follow it.
 */
export function compareTreeNodes(a: TreeNode, b: TreeNode): number {
  const rank = (n: TreeNode) => (n.kind === "folder" ? 0 : 1);
  return rank(a) - rank(b) || collator.compare(a.name, b.name) || a.id.localeCompare(b.id);
}

export function knowledgeTree(input: {
  documents?: readonly KnowledgeDocument[];
  pages?: readonly WikiPageSummary[];
  folders?: readonly Folder[];
  /** The open conversation's artefacts the viewer may read. */
  artefacts?: readonly ArtefactSummary[];
  attachments?: readonly Attachment[];
}): KnowledgeTree {
  const byId = new Map<string, TreeNode>();
  const section = (s: TreeSection, name: string): SectionNode => {
    const node: SectionNode = {
      kind: "section",
      id: sectionId(s),
      parentId: null,
      section: s,
      name,
      folded: foldTitle(name),
      children: [],
      count: 0,
    };
    byId.set(node.id, node);
    return node;
  };
  // Names are the caller's to translate: the model keeps the section key as the name.
  const files = section("files", "files");
  const wiki = section("wiki", "wiki");
  const connected = section("connected", "connected");
  const attachments = section("attachments", "attachments");
  const artefacts = section("artefacts", "artefacts");
  const roots: Record<FolderSection, SectionNode> = { files, wiki };

  const folders = new Map((input.folders ?? []).map((f) => [f.id, f]));
  for (const f of folders.values()) {
    const node: FolderNode = {
      kind: "folder",
      id: folderNodeId(f.id),
      parentId: null,
      section: f.section,
      name: f.name,
      folded: foldTitle(f.name),
      folder: f,
      depth: 1,
      children: [],
      count: 0,
    };
    byId.set(node.id, node);
  }
  // Attach each folder to its parent, or to its section's root when the parent is unknown (or
  // of the other section, or would close a cycle: the server prevents both).
  for (const f of folders.values()) {
    const node = byId.get(folderNodeId(f.id)) as FolderNode;
    const parent = f.parentId ? byId.get(folderNodeId(f.parentId)) : undefined;
    const ok =
      parent?.kind === "folder" && parent.section === f.section && !closesCycle(f, folders);
    const holder = ok ? (parent as FolderNode) : roots[f.section];
    node.parentId = holder.id;
    holder.children.push(node);
  }

  const place = (item: ItemNode, folderId: string | null | undefined, root: SectionNode) => {
    const folder = folderId ? byId.get(folderNodeId(folderId)) : undefined;
    // A folder created since the last read is unknown: the item waits at the root.
    const holder = folder?.kind === "folder" && folder.section === root.section ? folder : root;
    item.parentId = holder.id;
    holder.children.push(item);
    byId.set(item.id, item);
  };
  for (const d of input.documents ?? []) {
    const type = d.connectorItemId ? "connected" : "file";
    const item: ItemNode = {
      kind: "item",
      type,
      id: `d:${d.id}`,
      parentId: null,
      section: type === "connected" ? "connected" : "files",
      name: d.title,
      folded: foldTitle(d.title),
      document: d,
    };
    // Connected items keep the connector's own structure later; for now they sit flat.
    place(item, type === "connected" ? null : d.folderId, type === "connected" ? connected : files);
  }
  for (const p of input.pages ?? []) {
    const item: ItemNode = {
      kind: "item",
      type: "wiki",
      id: `w:${p.id}`,
      parentId: null,
      section: "wiki",
      name: p.title,
      folded: foldTitle(p.title),
      page: p,
    };
    place(item, p.folderId, wiki);
  }

  for (const a of input.attachments ?? []) {
    place(
      {
        kind: "item",
        type: "attachment",
        id: `t:${a.id}`,
        parentId: null,
        section: "attachments",
        name: a.title,
        folded: foldTitle(a.title),
        attachment: a,
      },
      null,
      attachments,
    );
  }

  // No folders: the conversation's artefacts, newest first, each at its latest readable version.
  for (const a of [...(input.artefacts ?? [])].sort(
    (x, y) => y.updatedAt.localeCompare(x.updatedAt) || x.id.localeCompare(y.id),
  )) {
    const item: ItemNode = {
      kind: "item",
      type: "artefact",
      id: `a:${a.id}`,
      parentId: artefacts.id,
      section: "artefacts",
      name: a.title,
      folded: foldTitle(a.title),
      artefact: a,
    };
    artefacts.children.push(item);
    byId.set(item.id, item);
  }
  artefacts.count = artefacts.children.length;

  const finish = (node: BranchNode, depth: number): number => {
    node.children.sort(compareTreeNodes);
    let count = 0;
    for (const child of node.children) {
      if (child.kind === "item") {
        count += 1;
      } else if (child.kind === "folder") {
        child.depth = depth + 1;
        count += finish(child, depth + 1);
      }
    }
    node.count = count;
    return count;
  };
  for (const s of [files, wiki, connected, attachments]) {
    finish(s, 0);
  }
  const hasConnected = connected.children.length > 0;
  return {
    sections: [
      wiki,
      files,
      ...(hasConnected ? [connected] : []),
      ...(attachments.children.length > 0 ? [attachments] : []),
      ...(artefacts.children.length > 0 ? [artefacts] : []),
    ],
    byId,
    hasConnected,
  };
}

function closesCycle(start: Folder, folders: ReadonlyMap<string, Folder>): boolean {
  const seen = new Set<string>([start.id]);
  let at = start.parentId ? folders.get(start.parentId) : undefined;
  while (at) {
    if (seen.has(at.id)) {
      return true;
    }
    seen.add(at.id);
    at = at.parentId ? folders.get(at.parentId) : undefined;
  }
  return false;
}

/** The node's ancestors, nearest last, from its section (excluded) down to its parent. */
export function folderPath(tree: KnowledgeTree, node: TreeNode): FolderNode[] {
  const path: FolderNode[] = [];
  let at = node.parentId ? tree.byId.get(node.parentId) : undefined;
  while (at && at.kind === "folder") {
    path.unshift(at);
    at = at.parentId ? tree.byId.get(at.parentId) : undefined;
  }
  return path;
}

/** How many folder levels a folder holds below it (0 for a folder without subfolders). */
export function subtreeHeight(node: FolderNode): number {
  let height = 0;
  for (const child of node.children) {
    if (child.kind === "folder") {
      height = Math.max(height, 1 + subtreeHeight(child));
    }
  }
  return height;
}

export type TreeSearch = {
  query: string;
  /** Nodes whose name contains the query (items and folders). */
  matches: ReadonlySet<string>;
  /** Nodes shown: matches, their ancestors, and everything under a matching folder. */
  shown: ReadonlySet<string>;
  /** Branches on a match's path, expanded for the search only (design §3). */
  expanded: ReadonlySet<string>;
  /** Matching items under each branch (a matching folder counts all its items). */
  counts: ReadonlyMap<string, number>;
  /** Matching items in the whole tree. */
  total: number;
};

/**
 * The tree narrowed to a query, any case or accent: a node shows if it or a descendant matches;
 * a matching folder shows with its subtree collapsed but expandable. Null for an empty query.
 * `only` narrows it to the items it accepts (the Sources tab's status filter): then folders never
 * match by name, so a folder shows only around accepted items, and an empty query lists them all.
 */
export function searchTree(
  tree: KnowledgeTree,
  query: string,
  only?: ((item: ItemNode) => boolean) | null,
): TreeSearch | null {
  const needle = foldTitle(query.trim());
  if (!needle && !only) {
    return null;
  }
  const hit = (node: TreeNode): boolean =>
    node.kind === "item"
      ? (!needle || node.folded.includes(needle)) && (!only || only(node))
      : !only && node.kind !== "section" && node.folded.includes(needle);
  const matches = new Set<string>();
  const shown = new Set<string>();
  const expanded = new Set<string>();
  const counts = new Map<string, number>();
  const include = (node: TreeNode): number => {
    shown.add(node.id);
    if (node.kind === "item") {
      return 1;
    }
    let n = 0;
    for (const child of node.children) {
      n += include(child);
    }
    counts.set(node.id, n);
    return n;
  };
  // Inside a matching folder everything shows, collapsed except the paths to deeper matches.
  const mark = (node: TreeNode): boolean => {
    const self = hit(node);
    if (self) {
      matches.add(node.id);
    }
    if (node.kind === "item") {
      return self;
    }
    const below = node.children.map(mark).some(Boolean);
    if (below) {
      expanded.add(node.id);
    }
    return self || below;
  };
  // Returns the matching items under the node, and marks what shows.
  const visit = (node: TreeNode): { items: number; any: boolean } => {
    const self = hit(node);
    if (node.kind === "item") {
      if (self) {
        matches.add(node.id);
        shown.add(node.id);
      }
      return { items: self ? 1 : 0, any: self };
    }
    if (self) {
      matches.add(node.id);
      if (node.children.map(mark).some(Boolean)) {
        expanded.add(node.id);
      }
      return { items: include(node), any: true };
    }
    let items = 0;
    let any = false;
    for (const child of node.children) {
      const r = visit(child);
      items += r.items;
      any ||= r.any;
    }
    if (any) {
      shown.add(node.id);
      expanded.add(node.id);
      counts.set(node.id, items);
    }
    return { items, any };
  };
  let total = 0;
  for (const s of tree.sections) {
    total += visit(s).items;
  }
  return { query, matches, shown, expanded, counts, total };
}

/** One visible row of the flattened tree, with what the ARIA tree pattern needs. */
export type TreeRow = {
  node: TreeNode;
  /** 1-based `aria-level`, relative to the shown root. */
  level: number;
  /** `aria-setsize` and `aria-posinset` among the node's shown siblings. */
  setSize: number;
  posInSet: number;
  /** For sections and folders: whether its children are shown. */
  expanded?: boolean;
  /** The count shown beside a branch: its items, or its matches while searching. */
  count?: number;
};

/**
 * The visible nodes in order (design §5): the children of `root` (a drilled folder, narrow
 * screens) or the sections, walked through the expanded branches. While searching, only shown
 * nodes are listed and the search's own expansion is added to the user's.
 */
export function visibleRows(
  tree: KnowledgeTree,
  expanded: ReadonlySet<string>,
  root: string | null = null,
  search: TreeSearch | null = null,
  sections: readonly TreeSection[] | null = null,
): TreeRow[] {
  const rows: TreeRow[] = [];
  const open = (id: string) => expanded.has(id) || (search?.expanded.has(id) ?? false);
  const walk = (nodes: readonly TreeNode[], level: number) => {
    const shown = search ? nodes.filter((n) => search.shown.has(n.id)) : nodes;
    shown.forEach((node, i) => {
      const branch = node.kind !== "item";
      const isOpen = branch && open(node.id);
      rows.push({
        node,
        level,
        setSize: shown.length,
        posInSet: i + 1,
        ...(branch && {
          expanded: isOpen,
          count: search ? (search.counts.get(node.id) ?? 0) : node.count,
        }),
      });
      if (isOpen) {
        walk(node.children, level + 1);
      }
    });
  };
  const top = root ? tree.byId.get(root) : undefined;
  if (top && top.kind !== "item") {
    walk(top.children, 1);
  } else {
    walk(sections ? tree.sections.filter((s) => sections.includes(s.section)) : tree.sections, 1);
  }
  return rows;
}

/**
 * The kept state of one project's tree in this browser (design §10): IDs only. `collapsed`:
 * sections shown expanded unless folded here (Artefacts, which comes and goes with the
 * conversation, so it cannot live in `expanded`).
 */
export type TreeState = { expanded: string[]; root: string | null; collapsed?: string[] };

/** Sections expanded by default that a user may fold (`artefacts-viewer`). */
export const FOLDABLE_DEFAULT_OPEN: readonly string[] = ["s:artefacts"];

export const treeStateKey = (projectId: string) => `esi:knowledge-tree:${projectId}`;
/** The former tab key: read once to open the section the user used, then removed. */
export const KNOWLEDGE_TAB_KEY = "esi:knowledge-tab";

const DEFAULT_EXPANDED = [sectionId("files"), sectionId("wiki")];

/**
 * The kept expansion and drilled root (defaults: Wiki and Files expanded, folders collapsed),
 * and the section a former tab asks to focus. Pure: the former key is removed on the first
 * write ({@link rememberTreeState}).
 */
export function rememberedTreeState(projectId: string): TreeState & { focus: string | null } {
  const tab = local.get(KNOWLEDGE_TAB_KEY);
  const focus = tab === "files" || tab === "wiki" ? sectionId(tab) : null;
  try {
    const value: unknown = JSON.parse(local.get(treeStateKey(projectId)) ?? "null");
    if (value && typeof value === "object") {
      const v = value as { expanded?: unknown; root?: unknown; collapsed?: unknown };
      const expanded = Array.isArray(v.expanded)
        ? v.expanded.filter((id): id is string => typeof id === "string")
        : DEFAULT_EXPANDED;
      const collapsed = Array.isArray(v.collapsed)
        ? v.collapsed.filter((id): id is string => FOLDABLE_DEFAULT_OPEN.includes(id))
        : [];
      return { expanded, root: typeof v.root === "string" ? v.root : null, collapsed, focus };
    }
  } catch {
    // A malformed entry: the defaults.
  }
  return {
    expanded:
      focus && !DEFAULT_EXPANDED.includes(focus) ? [...DEFAULT_EXPANDED, focus] : DEFAULT_EXPANDED,
    root: null,
    focus,
  };
}

export function rememberTreeState(projectId: string, state: TreeState): void {
  local.set(treeStateKey(projectId), JSON.stringify(state));
  local.set(KNOWLEDGE_TAB_KEY, null);
}

/** The kept state without IDs the tree no longer has (a deleted folder, a lost section). */
export function knownState(tree: KnowledgeTree, state: TreeState): TreeState {
  const known = (id: string) => {
    const node = tree.byId.get(id);
    return node !== undefined && node.kind !== "item";
  };
  return {
    expanded: state.expanded.filter(known),
    root: state.root && tree.byId.get(state.root)?.kind === "folder" ? state.root : null,
    // Kept whatever this tree holds: Artefacts is absent from a conversation without any.
    collapsed: state.collapsed ?? [],
  };
}

/** A Wiki folder's index: its subfolders and the pages directly in it, in the tree's order. */
export type WikiFolderIndex = {
  folder: Folder;
  /** Its names from the Wiki root, itself last. */
  path: string[];
  subfolders: Folder[];
  pages: WikiPageSummary[];
};

/**
 * The index of Wiki folder `folderId` from the reader-filtered page index and the folder list
 * (both already loaded by the tree): null for an unknown folder or one of Files. It lists only
 * the pages the viewer received, so it never counts one they cannot read.
 */
export function wikiFolderIndex(
  pages: readonly WikiPageSummary[],
  folders: readonly Folder[],
  folderId: string,
): WikiFolderIndex | null {
  const tree = knowledgeTree({ pages, folders });
  const node = tree.byId.get(folderNodeId(folderId));
  if (node?.kind !== "folder" || node.section !== "wiki") {
    return null;
  }
  return {
    folder: node.folder,
    path: [...folderPath(tree, node).map((f) => f.name), node.name],
    subfolders: node.children.flatMap((c) => (c.kind === "folder" ? [c.folder] : [])),
    pages: node.children.flatMap((c) => (c.kind === "item" && c.type === "wiki" ? [c.page] : [])),
  };
}

/**
 * The previous and next readable pages of `pageId` in its folder (or the Wiki root), in the
 * tree's order: none before the first, none after the last, neither for a page alone.
 */
export function wikiNeighbours(
  pages: readonly WikiPageSummary[],
  folders: readonly Folder[],
  pageId: string,
): { previous: WikiPageSummary | null; next: WikiPageSummary | null } {
  const tree = knowledgeTree({ pages, folders });
  const node = tree.byId.get(`w:${pageId}`);
  const parent = node?.parentId ? tree.byId.get(node.parentId) : undefined;
  const siblings =
    parent && parent.kind !== "item"
      ? parent.children.flatMap((c) => (c.kind === "item" && c.type === "wiki" ? [c.page] : []))
      : [];
  const at = siblings.findIndex((p) => p.id === pageId);
  return at < 0
    ? { previous: null, next: null }
    : { previous: siblings[at - 1] ?? null, next: siblings[at + 1] ?? null };
}
